import { useState } from 'react'
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { companiesApi, serversApi, usersApi } from '../../api'
import { boardTeamsNotifyApi } from '../../api/board'
import { getErrorMessage } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import type { Company, EdgeServer, User, RegistrationStatus } from '../../types'
import { EDGE_APPS, APP_BOARD } from '../../config/apps'

const sameApps = (a: string[], b: string[]) => a.length === b.length && a.every(x => b.includes(x))

export default function PortalCompanyDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { role } = useAuthStore()
  const [tab, setTab] = useState<'servers' | 'users'>('servers')

  const { data: company, isLoading, isError } = useQuery({
    queryKey: ['companies', id],
    queryFn: () => companiesApi.getById(id).then(r => r.data),
    enabled: !!id,
  })

  if (!id) return <Navigate to="/admin/companies" replace />

  return (
    <div>
      <div className="mb-4">
        <Link to="/admin/companies" className="text-xs text-gray-500 hover:text-gray-700">← 회사 목록</Link>
      </div>

      {isLoading && <div className="text-sm text-gray-400">불러오는 중...</div>}
      {isError && <div className="text-sm text-red-500">회사 정보를 불러올 수 없습니다.</div>}

      {company && (
        <>
          <CompanyInfoSection
            company={company}
            canInviteAdmin={role === 'Admin'}
            onDeleted={() => navigate('/admin/companies')}
          />

          {/* 보드를 구독한 회사만 — 저장은 보드 API(보드 배경 작업이 토큰 없이 읽어야 하므로) */}
          {company.enabledApps.includes(APP_BOARD) && <TeamsNotifySection companyId={id} />}

          <div className="mt-8 border-b border-gray-200">
            <div className="flex gap-6">
              <TabButton active={tab === 'servers'} onClick={() => setTab('servers')}>Edge 서버</TabButton>
              <TabButton active={tab === 'users'} onClick={() => setTab('users')}>회원</TabButton>
            </div>
          </div>

          <div className="mt-6">
            {tab === 'servers' && <ServersTab companyId={id} />}
            {tab === 'users' && <UsersTab companyId={id} canInvite={role === 'Manager'} />}
          </div>
        </>
      )}
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`pb-3 text-sm font-medium transition border-b-2 ${
        active ? 'border-teal-700 text-teal-800' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}>
      {children}
    </button>
  )
}

function CompanyInfoSection({
  company, canInviteAdmin, onDeleted,
}: {
  company: Company
  canInviteAdmin: boolean
  onDeleted: () => void
}) {
  const qc = useQueryClient()
  const initialForm = () => ({
    name: company.name, code: company.code, isActive: company.isActive,
    enabledApps: company.enabledApps ?? [],
  })
  const [form, setForm] = useState(initialForm)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')

  const dirty = form.name !== company.name || form.code !== company.code || form.isActive !== company.isActive
    || !sameApps(form.enabledApps, company.enabledApps ?? [])

  const toggleApp = (id: string, on: boolean) =>
    setForm(f => ({ ...f, enabledApps: on ? [...f.enabledApps, id] : f.enabledApps.filter(a => a !== id) }))

  const updateMutation = useMutation({
    mutationFn: () => companiesApi.update(company.id, form.name, form.code, form.isActive, form.enabledApps),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      qc.invalidateQueries({ queryKey: ['companies', company.id] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => companiesApi.delete(company.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['companies'] }); onDeleted() },
  })

  const inviteMutation = useMutation({
    mutationFn: () => companiesApi.invite(company.id, inviteEmail),
    onSuccess: () => { setShowInvite(false); setInviteEmail(''); alert('초대 메일 발송 완료') },
    onError: (err) => alert(getErrorMessage(err, '초대 발송에 실패했습니다.')),
  })

  return (
    <div className="bg-surface rounded-xl border border-gray-200/80 shadow-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">{company.name}</h2>
          <p className="text-xs font-mono text-gray-400 mt-1">{company.code}</p>
        </div>
        <div className="flex gap-2">
          {canInviteAdmin && (
            <button onClick={() => setShowInvite(true)}
              className="text-xs text-teal-700 border border-teal-600/30 px-3 py-1.5 rounded-lg hover:bg-teal-50 transition">
              관리자 초대
            </button>
          )}
          <button onClick={() => { if (confirm('이 회사를 삭제하시겠습니까?')) deleteMutation.mutate() }}
            className="text-xs text-red-500 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 transition">
            회사 삭제
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">회사명</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/25 focus:border-teal-600" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">회사 코드</label>
          <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-600/25 focus:border-teal-600" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">상태</label>
          <select value={form.isActive ? '1' : '0'}
            onChange={e => setForm({ ...form, isActive: e.target.value === '1' })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="1">활성</option>
            <option value="0">비활성</option>
          </select>
        </div>
      </div>

      {/* 사용 앱 — 로그인 토큰 aud 로 발급되어 각 앱 접근 권한이 된다. 끄면 다음 토큰 갱신부터 차단. */}
      <div className="mt-4">
        <label className="block text-xs text-gray-500 mb-2">사용 서비스</label>
        <div className="flex flex-wrap gap-3">
          {EDGE_APPS.map(app => {
            const on = form.enabledApps.includes(app.id)
            return (
              <label key={app.id}
                className={`flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-lg border cursor-pointer transition ${
                  on ? 'border-teal-600/40 bg-teal-50/60' : 'border-gray-200 bg-surface hover:border-gray-300'
                }`}>
                <input type="checkbox" checked={on}
                  onChange={e => toggleApp(app.id, e.target.checked)}
                  className="sr-only" />
                <span className={`w-8 h-8 rounded-md grid place-items-center text-[11px] font-black tracking-wide ${
                  on ? 'bg-[#14161a] text-white' : 'bg-gray-100 text-gray-400'
                }`}>{app.mark}</span>
                <span className="text-sm text-gray-800">{app.label}</span>
                <span className={`text-[11px] font-medium ${on ? 'text-teal-700' : 'text-gray-300'}`}>{on ? '사용' : '중지'}</span>
              </label>
            )
          })}
        </div>
        {form.enabledApps.length === 0 && (
          <p className="text-xs text-amber-600 mt-2">사용 서비스 미선택 시 앱 접근 불가</p>
        )}
      </div>

      {dirty && (
        <div className="flex gap-2 mt-4">
          <button onClick={() => updateMutation.mutate()}
            className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition">저장</button>
          <button onClick={() => setForm(initialForm())}
            className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">되돌리기</button>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-4">매니저 초대</h3>
            <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              type="email" placeholder="초대할 이메일"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={() => inviteMutation.mutate()}
                disabled={!inviteEmail}
                className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition disabled:opacity-50">초대 발송</button>
              <button onClick={() => { setShowInvite(false); setInviteEmail('') }}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const STATUS_LABELS: Record<RegistrationStatus, string> = {
  PendingApproval: '승인 대기',
  Issued: '발급 완료',
  Registered: '등록 완료',
}
const STATUS_COLORS: Record<RegistrationStatus, string> = {
  PendingApproval: 'bg-yellow-100 text-yellow-700',
  Issued: 'bg-blue-100 text-blue-700',
  Registered: 'bg-green-100 text-green-700',
}

function ServersTab({ companyId }: { companyId: string }) {
  const { data: allServers = [] } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.getAll().then(r => r.data),
  })

  const servers = allServers.filter((s: EdgeServer) => s.companyId === companyId)

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">
          Edge 서버 <span className="text-gray-400 font-normal">({servers.length})</span>
        </h3>
      </div>

      {servers.length === 0 ? (
        <div className="bg-surface rounded-xl border border-gray-200/80 shadow-sm p-8 text-center text-gray-400 text-sm">
          이 회사에 등록된 Edge 서버가 없습니다.
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-gray-200/80 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs">
              <tr>
                <th className="text-left px-4 py-3">서버명</th>
                <th className="text-left px-4 py-3">IP</th>
                <th className="text-left px-4 py-3">메모</th>
                <th className="text-left px-4 py-3">연결</th>
                <th className="text-left px-4 py-3">상태</th>
                <th className="text-left px-4 py-3">마지막 통신</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {servers.map((s: EdgeServer) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{s.name}</td>
                  <td className="px-4 py-3 text-gray-500">{s.ipAddress ?? '-'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.memo ?? '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.isOnline ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {s.isOnline ? '온라인' : '오프라인'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status]}`}>
                      {STATUS_LABELS[s.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {s.lastHeartbeat ? new Date(s.lastHeartbeat).toLocaleString('ko-KR') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function UsersTab({ companyId, canInvite }: { companyId: string; canInvite: boolean }) {
  const qc = useQueryClient()
  const { role } = useAuthStore()
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [editUser, setEditUser] = useState<User | null>(null)

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll().then(r => r.data as User[]),
  })

  const users = allUsers.filter(u => u.companyId === companyId)

  const inviteMutation = useMutation({
    mutationFn: () => usersApi.invite(inviteEmail),
    onError: (err) => alert(getErrorMessage(err, '초대 발송에 실패했습니다.')),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setShowInvite(false); setInviteEmail('')
      alert('초대 메일 발송 완료')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (u: User) => usersApi.update(u.id, u.name, u.role),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setEditUser(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const roleLabel = (r: string) => ({ Admin: '관리자', Manager: '매니저', User: '사용자' }[r] ?? r)
  const roleColor = (r: string) => ({
    Admin: 'bg-purple-100 text-purple-700',
    Manager: 'bg-teal-100 text-teal-800',
    User: 'bg-gray-100 text-gray-600',
  }[r] ?? 'bg-gray-100 text-gray-600')

  return (
    <div>
      {canInvite && (
        <div className="flex justify-end mb-4">
          <button onClick={() => setShowInvite(true)}
            className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition">
            + 사용자 초대
          </button>
        </div>
      )}

      {users.length === 0 ? (
        <div className="bg-surface rounded-xl border border-gray-200/80 shadow-sm p-8 text-center text-gray-400 text-sm">
          회원이 없습니다.
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-gray-200/80 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs">
              <tr>
                <th className="text-left px-4 py-3">이름</th>
                <th className="text-left px-4 py-3">이메일</th>
                <th className="text-left px-4 py-3">권한</th>
                <th className="text-left px-4 py-3">이메일 인증</th>
                <th className="text-left px-4 py-3">가입일</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColor(u.role)}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.isEmailVerified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {u.isEmailVerified ? '인증됨' : '미인증'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{new Date(u.createdAt).toLocaleDateString('ko-KR')}</td>
                  <td className="px-4 py-3 text-right flex gap-2 justify-end">
                    <button onClick={() => setEditUser({ ...u })} className="text-teal-700 hover:text-teal-900 text-xs">수정</button>
                    <button onClick={() => deleteMutation.mutate(u.id)} className="text-red-400 hover:text-red-600 text-xs">삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-4">사용자 초대</h3>
            <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              type="email" placeholder="초대할 이메일 주소"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={() => inviteMutation.mutate()}
                disabled={!inviteEmail}
                className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition disabled:opacity-50">
                초대 발송
              </button>
              <button onClick={() => { setShowInvite(false); setInviteEmail('') }}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-4">회원 수정</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">이름</label>
                <input value={editUser.name} onChange={e => setEditUser({ ...editUser, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">권한</label>
                <select value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value as User['role'] })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="User">사용자</option>
                  <option value="Manager">매니저</option>
                  {role === 'Admin' && <option value="Admin">관리자</option>}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => updateMutation.mutate(editUser)}
                className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition">저장</button>
              <button onClick={() => setEditUser(null)}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * 보드의 Teams 메시지 알림 — 카드 담당자 지정(type 1)과 아침 기한 알림(type 2)을
 * 회사의 Power Automate 워크플로 하나로 보낸다. 회사마다 Teams 테넌트·워크플로가 달라 회사 단위 설정이다.
 * 저장소는 보드 DB 이고 이 화면이 보드 API 를 직접 부른다(같은 EDGE 토큰, aud 에 board 포함).
 */
function TeamsNotifySection({ companyId }: { companyId: string }) {
  const qc = useQueryClient()
  const [url, setUrl] = useState<string | null>(null) // null = 서버 값 그대로
  const [tested, setTested] = useState<'ok' | 'fail' | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['board-teams-notify', companyId],
    queryFn: () => boardTeamsNotifyApi.get(companyId).then(r => r.data),
  })

  const save = useMutation({
    mutationFn: (v: { enabled: boolean; webhookUrl?: string }) =>
      boardTeamsNotifyApi.save(companyId, v.enabled, v.webhookUrl).then(r => r.data),
    onSuccess: (row) => {
      qc.setQueryData(['board-teams-notify', companyId], row)
      setUrl(null)
      setTested(null)
    },
    onError: (err) => alert(getErrorMessage(err, '설정을 저장하지 못했습니다.')),
  })

  const test = useMutation({
    mutationFn: () => boardTeamsNotifyApi.test(companyId).then(r => r.data),
    onSuccess: (r) => setTested(r.sent ? 'ok' : 'fail'),
    onError: (err) => alert(getErrorMessage(err, '테스트 메시지를 보내지 못했습니다.')),
  })

  if (isLoading) return null
  if (isError || !data)
    return (
      <div className="mt-6 bg-surface rounded-xl border border-gray-200/80 shadow-sm p-6">
        <h3 className="font-semibold text-gray-800">Teams 알림</h3>
        <p className="text-xs text-gray-400 mt-2">보드 설정을 불러올 수 없습니다. 보드 접근 권한이 있는 계정인지 확인해 주세요.</p>
      </div>
    )

  const value = url ?? data.webhookUrl
  const dirty = url !== null && url.trim() !== data.webhookUrl
  const busy = save.isPending || test.isPending

  return (
    <div className="mt-6 bg-surface rounded-xl border border-gray-200/80 shadow-sm p-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">Teams 알림</h3>
          <p className="text-xs text-gray-400 mt-1">
            카드 담당자 지정 · 평일 아침 기한 알림을 이 회사의 워크플로로 보냅니다.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0">
          <input type="checkbox" checked={data.enabled} disabled={busy}
            onChange={e => save.mutate({ enabled: e.target.checked })}
            className="accent-teal-700" />
          <span className="text-sm text-gray-700">{data.enabled ? '사용' : '중지'}</span>
        </label>
      </div>

      {data.enabled && !data.configured && (
        <p className="text-xs text-amber-600 mt-3">워크플로 주소가 없어 메시지가 나가지 않습니다.</p>
      )}

      <div className="mt-4">
        <label className="block text-xs text-gray-500 mb-1">워크플로 주소 (Power Automate HTTP 트리거)</label>
        <div className="flex gap-2">
          <input value={value} onChange={e => setUrl(e.target.value)}
            placeholder="https://prod-00.koreacentral.logic.azure.com/workflows/..."
            className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-600/25 focus:border-teal-600" />
          {dirty ? (
            <>
              <button onClick={() => save.mutate({ enabled: data.enabled, webhookUrl: (url ?? '').trim() })}
                disabled={busy}
                className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition disabled:opacity-50">저장</button>
              <button onClick={() => setUrl(null)}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">되돌리기</button>
            </>
          ) : (
            <button onClick={() => test.mutate()} disabled={busy || !data.configured}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
              {tested === 'ok' ? '보냄' : '테스트'}
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          담당자 지정(<span className="font-mono">alertType 0</span>)과 기한 알림(<span className="font-mono">alertType 1</span>)이 이 주소 하나로 갑니다.
          비우면 보드 서버 기본값을 씁니다. 메시지 링크 주소는 <span className="font-mono">{data.webBaseUrl || '(미설정)'}</span> 입니다.
        </p>
        {tested === 'fail' && <p className="text-xs text-red-500 mt-1">보내지 못했습니다. 주소를 확인해 주세요.</p>}
      </div>
    </div>
  )
}
